import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArtworkCardProps } from "../types";
import { Utils } from "../utils";
import { usePersistStore, useStore } from "../store";
import Avatar from "./Avatar";
import Confirm from "./Confirm";
import useAdUtils from "../hooks/useAdUtils";
import { useAddToWatchlist, useRemoveFromWatchlist } from "../hooks/queries";
import { setRedirectToCurrentLocation } from "../utils/postLoginRedirect";

const ArtworkCard: React.FC<ArtworkCardProps> = (data: ArtworkCardProps) => {
  const navigate = useNavigate();
  const [showConfirmPopup, setShowConfirmPopup] = useState<boolean>(false);
  const { token, user, watchlist } = usePersistStore((s: any) => s);
  const { setErrorMessage, setSuccessMessage } = useStore((s: any) => s);
  const [isFavourite, setIsFavourite] = useState<boolean>(false);
  const addToWatchlist = useAddToWatchlist();
  const removeFromWatchlist = useRemoveFromWatchlist();

  useEffect(() => {
    if (!token) return;
    if (watchlist?.includes(data._id)) setIsFavourite(true);
  }, [watchlist, data._id, token]);

  const { isOpenForBids, isClosed, isUpcoming, isComingSoon, isSold } =
    useAdUtils(data);

  const sellerProfileUrl = `/seller/${data?.ownerId}`;
  const detailsUrl = `/details/${data?._id}`;

  const handleAddToWatchlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!token || !user) {
      setRedirectToCurrentLocation();
      navigate("/login");
      return;
    }
    // Optimistic; the mutation owns the store update, toast and invalidation.
    setIsFavourite(true);
    addToWatchlist.mutate(data?._id as string, {
      onSuccess: () => data?.reload?.(),
      onError: () => setIsFavourite(false),
    });
  };

  const handleRemoveFromWatchlist = () => {
    if (!token || !user || !data?._id) {
      setRedirectToCurrentLocation();
      navigate("/login");
      return;
    }
    setIsFavourite(false);
    setShowConfirmPopup(false);
    removeFromWatchlist.mutate(data?._id, {
      onSuccess: () => data?.reload?.(),
      onError: () => setIsFavourite(true),
    });
  };

  const handleBookmarkClick = (e: React.MouseEvent) => {
    if (isFavourite) {
      e.stopPropagation();
      setShowConfirmPopup(true);
    } else {
      handleAddToWatchlist(e);
    }
  };

  const handleShareClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}${detailsUrl}`;
    const shareData = {
      title: data?.title || "Boll-e™ artwork",
      text: data?.title
        ? `Check out "${data.title}" on Boll-e™`
        : "Check out this artwork on Boll-e™",
      url: shareUrl,
    };

    // navigator.share is gated on a secure context and on browsers that
    // actually accept this payload — Firefox desktop exposes share() but
    // returns false from canShare(), so fall through to clipboard.
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.share === "function" &&
      (typeof navigator.canShare !== "function" ||
        navigator.canShare(shareData))
    ) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err: any) {
        if (err?.name === "AbortError") return;
        // fall through to clipboard on any other share failure
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setSuccessMessage("Link copied to clipboard.");
    } catch {
      setErrorMessage("Could not copy link. Please copy it manually.");
    }
  };

  // Pick the floating status badge for this card. isComingSoon is a
  // distinct state (APPROVED but not yet live) — don't collapse with the
  // plain upcoming bucket so users can see which auctions are still
  // awaiting admin activation vs. which are actually scheduled.
  const renderStatusBadge = () => {
    if (isOpenForBids) {
      return (
        <span className="artCard__statusBadge artCard__statusBadge--live">
          <span className="artCard__statusBadge__dot" /> Live · closing{" "}
          {Utils.formatFromNow(data?.biddingEnds)}
        </span>
      );
    }
    if (isComingSoon) {
      return (
        <span className="artCard__statusBadge artCard__statusBadge--comingSoon">
          <i className="bi bi-hourglass-split" aria-hidden="true" />
          Coming Soon
          {data?.biddingStarts ? (
            <> · opens {Utils.formatFromNow(data?.biddingStarts)}</>
          ) : null}
        </span>
      );
    }
    if (isUpcoming) {
      return (
        <span className="artCard__statusBadge artCard__statusBadge--upcoming">
          Opens {Utils.formatFromNow(data?.biddingStarts)}
        </span>
      );
    }
    if (isSold) {
      return (
        <span className="artCard__statusBadge artCard__statusBadge--sold">
          <i className="bi bi-check2-circle" aria-hidden="true" />
          Sold
          {data?.biddingEnds ? (
            <> · {Utils.formatFromNow(data?.biddingEnds)}</>
          ) : null}
        </span>
      );
    }
    if (isClosed) {
      return (
        <span className="artCard__statusBadge artCard__statusBadge--closed">
          Closed
        </span>
      );
    }
    return null;
  };

  const primaryLabel =
    isOpenForBids && data?.showBidNowButton ? "Bid Now" : "Check details";

  return (
    <>
      <Confirm
        show={showConfirmPopup}
        onNo={() => setShowConfirmPopup(false)}
        onYes={handleRemoveFromWatchlist}
        title="Unwatch"
        body="Are you sure you want to remove this artwork from your watchlist?"
        confirmButtonText="Yes"
      />
      <div className="artCard">
        <div
          className="artCard__media"
          onClick={() => navigate(detailsUrl)}
          role="button"
          tabIndex={0}
          aria-label={data?.title}
        >
          <img
            className="artCard__image"
            src={data?.imageUrl || "/assets/imgs/crawl.png"}
            alt={data?.title || ""}
          />
          {renderStatusBadge()}
          {(isOpenForBids || isUpcoming || isComingSoon) && (
            <button
              type="button"
              className={`artCard__bookmark ${
                isFavourite ? "artCard__bookmark--active" : ""
              }`}
              onClick={handleBookmarkClick}
              aria-label={isFavourite ? "Remove from watchlist" : "Add to watchlist"}
              title={isFavourite ? "Remove from watchlist" : "Add to watchlist"}
            >
              <i
                className={`bi ${isFavourite ? "bi-heart-fill" : "bi-heart"}`}
                aria-hidden="true"
              />
            </button>
          )}
        </div>
        <div className="artCard__body">
          <div className="artCard__topMeta">
            <span className="artCard__category">
              {data?.category}
              {data?.subCategory ? ` · ${data.subCategory}` : ""}
            </span>
            <div className="artCard__metaActions">
              <a
                className="artCard__share"
                href={detailsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                aria-label="Open details in new tab"
                title="Open in new tab"
              >
                <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
              </a>
              <button
                type="button"
                className="artCard__share"
                onClick={handleShareClick}
                aria-label="Share artwork"
                title="Share artwork"
              >
                <i className="bi bi-share" aria-hidden="true" />
              </button>
            </div>
          </div>
          <h3
            className="artCard__title"
            onClick={() => navigate(detailsUrl)}
            title={data?.title}
          >
            {data?.title} {data?.showFireEmoji && "️‍🔥"}
          </h3>
          <div className="artCard__seller">
            <Avatar
              src={data?.ownerImage}
              name={data?.ownerName}
              className="artCard__sellerAvatar"
              onClick={() => navigate(sellerProfileUrl)}
            />
            <span
              className="artCard__sellerName"
              onClick={() => navigate(sellerProfileUrl)}
            >
              {data?.ownerName}
            </span>
          </div>
          <div className="artCard__stats">
            <span>
              <i className="bi bi-eye" aria-hidden="true" />
              {Utils.formatToNumber(String(data?.views || 0))}
            </span>
            {data?.distance && (
              <span>
                <i className="bi bi-geo-alt" aria-hidden="true" />
                {Utils.formatToNumber2(data.distance.toString())} mi
              </span>
            )}
          </div>
          <div className="artCard__priceRow">
            <div>
              <div className="artCard__priceLabel">Opening bid</div>
              <div className="artCard__priceValue">
                {Utils.formatToCurrency(data?.price)}
              </div>
            </div>
          </div>
          <div className="artCard__actions">
            <button
              type="button"
              className={`artCard__primary ${
                isOpenForBids && data?.showBidNowButton
                  ? ""
                  : "artCard__primary--neutral"
              }`}
              onClick={() => navigate(detailsUrl)}
            >
              {primaryLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ArtworkCard;
